import React from "react";
import { Tour, Button, Space } from "antd";
import type { TourProps } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  hasSeenOnboardingTour,
  markOnboardingTourSeen,
  resetOnboardingTourSeen,
} from "../ux/uiPrefs";

type Props = {
  /** When true, auto-open if not seen yet. */
  autoStart?: boolean;
};

export function OnboardingTour({ autoStart = true }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (autoStart && !hasSeenOnboardingTour()) {
      setOpen(true);
    }
  }, [autoStart]);

  const steps: TourProps["steps"] = [
    {
      title: t("compliance.onboarding.steps.welcomeTitle"),
      description: t("compliance.onboarding.steps.welcomeBody"),
      target: () => document.querySelector("[data-tour='dds-case-list']") as HTMLElement,
    },
    {
      title: t("compliance.onboarding.steps.newCaseTitle"),
      description: t("compliance.onboarding.steps.newCaseBody"),
      target: () => document.querySelector("[data-tour='dds-new-case']") as HTMLElement,
    },
    {
      title: t("compliance.onboarding.steps.workflowTitle"),
      description: t("compliance.onboarding.steps.workflowBody"),
      target: () => document.querySelector("[data-tour='dds-case-list']") as HTMLElement,
    },
    {
      title: t("compliance.onboarding.steps.paletteTitle"),
      description: t("compliance.onboarding.steps.paletteBody"),
      target: () => document.querySelector("[data-tour='dds-command-palette']") as HTMLElement,
    },
  ];

  const finish = () => {
    markOnboardingTourSeen();
    setOpen(false);
  };

  return (
    <>
      <Button
        type="text"
        icon={<QuestionCircleOutlined />}
        aria-label={t("compliance.onboarding.replay")}
        data-testid="dds-tour-replay"
        onClick={() => {
          resetOnboardingTourSeen();
          setOpen(true);
        }}
      >
        {t("compliance.onboarding.replay")}
      </Button>
      <Tour
        open={open}
        onClose={finish}
        onFinish={finish}
        steps={steps}
        mask={{ style: { boxShadow: "inset 0 0 0 0 rgba(0,0,0,0.45)" } }}
      />
    </>
  );
}

/** Compact trigger for header without auto-start. */
export function OnboardingTourTrigger() {
  return (
    <Space size={0}>
      <OnboardingTour autoStart={false} />
    </Space>
  );
}
